import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {CommanServices} from "../services/commanServices";
import { SaaSServices } from "../services/saasServices";
import { unwrapApiData } from "../auth/saasSession";

/**
 * ImpersonationBanner
 *
 * Renders a full-width amber banner at the top of the page whenever an admin
 * is impersonating another user.  The banner shows the impersonated user's
 * name and an "Exit Impersonation" button that restores the admin session.
 *
 * Session storage contract (keys in localStorage):
 *   impersonating  – JSON { name: string }          set on enter
 *   admin_token    – string  (original admin JWT)    set on enter
 *   admin_user     – JSON string (original admin user object)  set on enter
 */
const ImpersonationBanner = () => {
    const navigate = useNavigate();
    const [impersonation, setImpersonation] = useState(null);
    const [ending, setEnding] = useState(false);

    const refresh = useCallback(() => {
        const raw = localStorage.getItem("impersonating");
        try {
            setImpersonation(raw ? JSON.parse(raw) : null);
        } catch (_) {
            localStorage.removeItem("impersonating");
            setImpersonation(null);
        }
    }, []);

    const handleExit = useCallback(async () => {
        if (ending) return;
        setEnding(true);
        const active = impersonation;
        const adminToken = localStorage.getItem("admin_token");
        const adminUser = localStorage.getItem("admin_user");

        let ended = !active?.session_id;
        if (active?.session_id) {
            try {
                ended = Boolean(unwrapApiData(
                    await SaaSServices.endImpersonationSession(active.session_id)
                ));
            } catch (_) {
                ended = false;
            }
        }

        // Restore the initiating administrator even if the target token has
        // just expired. Retry the audited termination with the admin token.
        if (adminToken) {
            localStorage.setItem("access_token", adminToken);
            CommanServices.setToken(adminToken);
        }
        if (adminUser) localStorage.setItem("user", adminUser);
        if (!ended && active?.session_id && adminToken) {
            try {
                await SaaSServices.endImpersonationSession(active.session_id);
            } catch (error) {
                console.error("Could not record impersonation termination", error);
            }
        }

        localStorage.removeItem("impersonating");
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
        window.dispatchEvent(new Event("impersonation-changed"));
        setEnding(false);
        navigate("/users", { replace: true });
    }, [ending, impersonation, navigate]);

    useEffect(() => {
        refresh();
        // Listen for same-tab localStorage changes fired by our custom event
        const handler = () => refresh();
        window.addEventListener("impersonation-changed", handler);
        // Also catch cross-tab storage events
        window.addEventListener("storage", refresh);
        return () => {
            window.removeEventListener("impersonation-changed", handler);
            window.removeEventListener("storage", refresh);
        };
    }, [refresh]);

    useEffect(() => {
        const expiresAt = Date.parse(impersonation?.expires_at || "");
        if (!Number.isFinite(expiresAt)) return undefined;
        const delay = Math.max(0, expiresAt - Date.now() - 250);
        const timer = window.setTimeout(() => { void handleExit(); }, delay);
        return () => window.clearTimeout(timer);
    }, [handleExit, impersonation?.expires_at]);

    if (!impersonation) return null;

    return (
        <div style={styles.banner}>
            <div style={styles.left}>
                <span style={styles.icon}>👤</span>
                <span style={styles.text}>
                    Impersonating: <strong>{impersonation.name}</strong>
                </span>
                {impersonation.ticket_number && (
                    <span style={styles.detail}>Reference: {impersonation.ticket_number}</span>
                )}
                <span style={styles.badge}>Admin View</span>
            </div>
            <button
                style={styles.exitBtn}
                onClick={handleExit}
                disabled={ending}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(0,0,0,0.25)";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(0,0,0,0.15)";
                }}
            >
                {ending ? "Ending…" : "✕ Exit Impersonation"}
            </button>
        </div>
    );
};

const styles = {
    banner: {
        position: "relative",
        zIndex: 1200,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 24px",
        background: "linear-gradient(90deg, #b45309 0%, #d97706 50%, #b45309 100%)",
        color: "#fff",
        fontSize: "14px",
        boxShadow: "0 2px 8px rgba(180, 83, 9, 0.4)",
        animation: "impersonation-slide-in 0.3s ease",
    },
    left: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },
    icon: {
        fontSize: "16px",
    },
    text: {
        letterSpacing: "0.02em",
    },
    badge: {
        background: "rgba(0,0,0,0.2)",
        borderRadius: "12px",
        padding: "2px 10px",
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
    },
    detail: {
        fontSize: "11px",
        opacity: 0.9,
    },
    exitBtn: {
        background: "rgba(0,0,0,0.15)",
        border: "1.5px solid rgba(255,255,255,0.5)",
        color: "#fff",
        padding: "6px 16px",
        borderRadius: "6px",
        cursor: "pointer",
        fontSize: "13px",
        fontWeight: 600,
        transition: "background 0.15s ease",
        whiteSpace: "nowrap",
    },
};

export default ImpersonationBanner;
